import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';

import type { PageQuery, PagedResult } from '@domain/models/paged-result.model';
import type { CategorySort, Store, StoreCategory } from '@domain/models/store.model';
import { StoreCategoryRepository } from '@domain/repositories/store-category.repository';

import type { ApiPagedResponse } from '@data/dto/api.dto';
import type { StoreCategoryDto, StoreDto } from '@data/dto/store.dto';
import {
  mapPagedStoreCategories,
  mapPagedStores,
  storeQueryParams,
} from '@data/mappers/store.mapper';

@Injectable()
export class StoreCategoryApiRepository extends StoreCategoryRepository {
  private readonly http = inject(HttpClient);
  private readonly path = 'api/store-categories';

  list(query: PageQuery, isAdmin = true): Observable<PagedResult<StoreCategory>> {
    const params = new HttpParams({
      fromObject: {
        ...storeQueryParams(query),
        isAdmin,
      } as Record<string, string | number | boolean>,
    });

    return this.http
      .get<ApiPagedResponse<StoreCategoryDto>>(this.path, { params })
      .pipe(map(mapPagedStoreCategories));
  }

  create(
    name: string,
    nameArabic: string,
    active: boolean,
    logoImage: string | null,
  ): Observable<void> {
    return this.http.post<void>(this.path, {
      name,
      nameArabic,
      active,
      logoImage,
    });
  }

  update(
    id: number,
    name: string,
    nameArabic: string,
    active: boolean,
    logoImage: string | null,
    sort: CategorySort,
  ): Observable<void> {
    return this.http.put<void>(`${this.path}/${id}`, {
      name,
      nameArabic,
      active,
      logoImage,
      storeSortCategory: sort,
    });
  }

  updateSort(id: number, sort: CategorySort): Observable<void> {
    return this.http.put<void>(`${this.path}/${id}/StoreSortCategory`, {
      storeSortCategory: sort,
    });
  }

  listStores(categoryId: number): Observable<PagedResult<Store>> {
    const params = new HttpParams({
      fromObject: { CategoryId: categoryId, limit: 1000, page: 1 },
    });

    return this.http.get<ApiPagedResponse<StoreDto>>('api/stores', { params }).pipe(map(mapPagedStores));
  }
}
